import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Star, X, MagnifyingGlass, DownloadSimple, Buildings, ChartLineUp, Lightbulb } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Sequence Library modal — browse coach-validated 80+ sequences across all
 * profiles and import one into a target campaign within the active profile.
 *
 * Open from EmailCampaignsPage header (Campagnes tab) via the new
 * "Library" button.
 */
export default function SequenceLibraryModal({ open, onClose, apiBase, activeProfile, campaigns, onImported }) {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [query, setQuery] = useState('');
  const [minScore, setMinScore] = useState(80);
  const [importTarget, setImportTarget] = useState({}); // {libraryId: campaignId}
  const [importing, setImporting] = useState(null);
  const [tab, setTab] = useState('browse'); // 'browse' | 'insights'
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsMode, setInsightsMode] = useState('coach'); // 'coach' | 'outcome'
  const [calibration, setCalibration] = useState(null);
  const [calibrationLoading, setCalibrationLoading] = useState(false);

  const loadCalibration = async () => {
    setCalibrationLoading(true);
    try {
      const r = await fetch(`${apiBase}/predictor/calibration`, { credentials: 'include' });
      const d = await r.json();
      setCalibration(d);
    } catch {
      /* silent */
    }
    setCalibrationLoading(false);
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const url = insightsMode === 'outcome'
        ? `${apiBase}/sequence-library/insights?outcome=true`
        : `${apiBase}/sequence-library/insights?min_score=80`;
      const r = await fetch(url, { credentials: 'include' });
      const d = await r.json();
      setInsights(d);
    } catch {
      toast.error('Kon insights niet laden');
    }
    setInsightsLoading(false);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ min_score: String(minScore), limit: '100', refresh: 'true' });
      if (query) params.set('q', query);
      const r = await fetch(`${apiBase}/sequence-library?${params}`, { credentials: 'include' });
      const d = await r.json();
      setEntries(d.entries || []);
    } catch {
      toast.error('Kon library niet laden');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      if (tab === 'browse') load();
      if (tab === 'insights') { loadInsights(); loadCalibration(); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, minScore, tab, insightsMode]);

  const importEntry = async (entry) => {
    const targetId = importTarget[entry.library_id];
    if (!targetId) {
      toast.error('Kies eerst een doelcampagne');
      return;
    }
    if (!window.confirm(`Importeer "${entry.name}" (score ${entry.overall_score}) als sequence in de geselecteerde campagne? Bestaande stappen worden vervangen.`)) return;
    setImporting(entry.library_id);
    try {
      const r = await fetch(`${apiBase}/sequence-library/${entry.library_id}/import`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_campaign_id: targetId, overwrite: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Import mislukt');
      toast.success(`Geïmporteerd: ${d.imported_steps} stappen`);
      onImported && onImported(targetId);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Import mislukt');
    }
    setImporting(null);
  };

  if (!open) return null;

  const scoreColor = (s) => {
    if (s >= 90) return { bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50 border-emerald-300' };
    if (s >= 80) return { bg: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-50 border-amber-300' };
    return { bg: 'bg-zinc-400', text: 'text-zinc-700', light: 'bg-zinc-50 border-zinc-300' };
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="sequence-library-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Star size={18} weight="fill" className="text-amber-500" />
              Sequence Library
            </h2>
            <p className="text-xs text-zinc-500">
              Cross-profile sequences die door de Coach gevalideerd zijn met score {minScore}+. Importeer een blueprint in een campagne van het profiel <strong>{activeProfile}</strong>.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded" data-testid="library-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pt-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-1">
          <button
            onClick={() => setTab('browse')}
            data-testid="library-tab-browse"
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${tab === 'browse' ? 'border-amber-500 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
          >
            <Star size={11} className="inline mr-1.5" /> Browse sequences
          </button>
          <button
            onClick={() => setTab('insights')}
            data-testid="library-tab-insights"
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${tab === 'insights' ? 'border-indigo-500 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
          >
            <ChartLineUp size={11} className="inline mr-1.5" /> Insights
          </button>
        </div>

        {tab === 'browse' && (
          <>
        <div className="px-6 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder="Zoek op product of doelgroep (bv. 'pijpbeugel', 'manege', 'cleaning')..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              className="pl-8 h-8 text-xs"
              data-testid="library-search-input"
            />
          </div>
          <select
            value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            className="text-xs border border-zinc-300 rounded-sm h-8 px-2 bg-white"
            data-testid="library-minscore-select"
          >
            <option value={80}>Min. score 80</option>
            <option value={85}>Min. score 85</option>
            <option value={90}>Min. score 90 (top)</option>
            <option value={70}>Min. score 70 (incl. amber)</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="library-search-btn">Zoek</Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {loading && <p className="text-xs text-zinc-500">Laden...</p>}
          {!loading && entries.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              <Star size={48} className="mx-auto mb-3 text-zinc-300" weight="duotone" />
              <p className="text-sm">Nog geen sequences in de library met score ≥ {minScore}.</p>
              <p className="text-xs mt-1">Tip: laat de Coach een sterke campagne scoren en hij komt automatisch hier terecht.</p>
            </div>
          )}
          {!loading && entries.map((entry) => {
            const sc = scoreColor(entry.overall_score);
            const isExternal = entry.source_profile_id !== activeProfile;
            return (
              <div key={entry.library_id} className={`border-2 ${sc.light} rounded-md p-4`} data-testid={`library-entry-${entry.library_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-sm text-zinc-900 truncate">{entry.name}</h3>
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-bold tracking-wide ${sc.text} ${sc.light} border`}>
                        {entry.overall_score}/100
                      </span>
                      {isExternal && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200" title={`Origineel uit profiel: ${entry.source_profile_id}`}>
                          <Buildings size={9} /> {entry.source_profile_id}
                        </span>
                      )}
                      {entry.usage_count > 0 && (
                        <span className="text-[10px] text-zinc-500">· {entry.usage_count}× geïmporteerd</span>
                      )}
                    </div>
                    {entry.product && (
                      <p className="text-xs text-zinc-600 mb-1"><strong>Product:</strong> {entry.product}</p>
                    )}
                    {entry.target_description && (
                      <p className="text-xs text-zinc-500 mb-1"><strong>Doelgroep:</strong> {entry.target_description}</p>
                    )}
                    {entry.outcome && entry.outcome.eligible && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5 mb-1" data-testid={`outcome-${entry.library_id}`}>
                        <span className="text-[9px] uppercase tracking-wide text-zinc-500 mr-1">Echte resultaten ({entry.outcome.sent} mails):</span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-[10px] text-blue-800">
                          📬 {entry.outcome.open_rate}% open
                        </span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-[10px] text-violet-800">
                          🖱 {entry.outcome.click_rate}% click
                        </span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-800 font-semibold">
                          💬 {entry.outcome.reply_rate}% reply
                        </span>
                        {entry.outcome.bounce_rate > 5 && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-[10px] text-rose-800">
                            ⚠ {entry.outcome.bounce_rate}% bounce
                          </span>
                        )}
                      </div>
                    )}
                    {entry.outcome && !entry.outcome.eligible && entry.outcome.sent === 0 && (
                      <p className="text-[10px] text-zinc-400 italic mt-1">Nog niet verzonden — geen IRL-data</p>
                    )}
                    {entry.overall_assessment && (
                      <p className="text-[11px] text-zinc-500 italic mt-1">"{entry.overall_assessment}"</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full ${sc.bg} text-white flex items-center justify-center font-data font-bold text-lg shadow`}>
                      {entry.overall_score}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mb-3">
                  {(entry.sequence_steps || []).map((s) => (
                    <div key={s.step} className="bg-white rounded-sm px-2 py-1.5 border border-zinc-200 text-[10px]" data-testid={`library-step-preview-${entry.library_id}-${s.step}`}>
                      <div className="font-semibold text-zinc-700">Stap {s.step}: {s.name}</div>
                      <div className="text-zinc-500 line-clamp-2" title={s.focus}>{s.focus?.slice(0, 80)}...</div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-zinc-200">
                  <select
                    value={importTarget[entry.library_id] || ''}
                    onChange={(e) => setImportTarget((m) => ({ ...m, [entry.library_id]: e.target.value }))}
                    className="text-xs border border-zinc-300 rounded-sm h-7 px-2 bg-white flex-1 max-w-xs"
                    data-testid={`library-target-select-${entry.library_id}`}
                  >
                    <option value="">Importeer naar campagne...</option>
                    {(campaigns || []).map((c) => (
                      <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    onClick={() => importEntry(entry)}
                    disabled={importing === entry.library_id || !importTarget[entry.library_id]}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                    data-testid={`library-import-btn-${entry.library_id}`}
                  >
                    <DownloadSimple size={12} className="mr-1" />
                    {importing === entry.library_id ? 'Importeren...' : 'Importeer'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        </>
        )}

        {tab === 'insights' && (
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="library-insights-panel">
            {/* PREDICTOR CALIBRATION — actual vs predicted reply-rate accuracy */}
            {(calibration || calibrationLoading) && (
              <div className="rounded-md border-2 border-sky-300 bg-gradient-to-br from-sky-50 to-cyan-50 p-4" data-testid="predictor-calibration-widget">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🎯</span>
                  <h3 className="text-sm font-bold text-sky-900">Predictor Calibration</h3>
                  <span className="text-[10px] uppercase text-zinc-500 tracking-wide">echte vs voorspelde reply-rate</span>
                </div>
                {calibrationLoading && <p className="text-xs text-sky-700 italic">Calibratie laden...</p>}
                {!calibrationLoading && calibration && calibration.n === 0 && (
                  <p className="text-xs text-zinc-600 italic" data-testid="calibration-empty">{calibration.note}</p>
                )}
                {!calibrationLoading && calibration && calibration.n > 0 && (
                  <>
                    <div className="grid grid-cols-4 gap-3 bg-white border border-sky-200 rounded-md p-3">
                      <div className="text-center">
                        <div className="text-[10px] uppercase text-zinc-500 tracking-wide">Sample</div>
                        <div className="font-data font-bold text-zinc-900 text-lg" data-testid="calibration-n">{calibration.n}</div>
                        <div className="text-[9px] text-zinc-400">campagnes</div>
                      </div>
                      <div className="text-center border-x border-zinc-100">
                        <div className="text-[10px] uppercase text-zinc-500 tracking-wide">MAE</div>
                        <div className={`font-data font-bold text-lg ${calibration.mae < 2 ? 'text-emerald-700' : calibration.mae < 5 ? 'text-amber-700' : 'text-rose-700'}`} data-testid="calibration-mae">
                          {calibration.mae}%
                        </div>
                        <div className="text-[9px] text-zinc-400">gem. afwijking</div>
                      </div>
                      <div className="text-center border-r border-zinc-100">
                        <div className="text-[10px] uppercase text-zinc-500 tracking-wide">Bias</div>
                        <div className={`font-data font-bold text-lg ${calibration.bias_direction === 'neutral' ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="calibration-bias">
                          {calibration.mean_error > 0 ? '+' : ''}{calibration.mean_error}%
                        </div>
                        <div className="text-[9px] text-zinc-400">{calibration.bias_direction === 'overestimate' ? 'overschat' : calibration.bias_direction === 'underestimate' ? 'onderschat' : 'neutraal'}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] uppercase text-zinc-500 tracking-wide">Status</div>
                        <div className="text-base mt-0.5">
                          {calibration.mae < 2 ? '🎯' : calibration.mae < 5 ? '⚠️' : '🚨'}
                        </div>
                        <div className="text-[9px] text-zinc-400">
                          {calibration.mae < 2 ? 'sterk' : calibration.mae < 5 ? 'redelijk' : 'zwak'}
                        </div>
                      </div>
                    </div>
                    {(calibration.recent || []).length > 0 && (
                      <div className="mt-3">
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5">
                          Recente retrospectives ({Math.min(calibration.recent.length, 10)})
                        </div>
                        <div className="space-y-1">
                          {calibration.recent.slice(0, 5).map((r, i) => (
                            <div key={r.campaign_id} className="flex items-center gap-2 text-[11px] px-2 py-1 rounded bg-white/80 border border-zinc-200" data-testid={`calibration-recent-${i}`}>
                              <span className="flex-1 truncate text-zinc-700">{r.label || r.campaign_id}</span>
                              <span className="font-data text-zinc-600">predicted <strong>{r.predicted ?? '–'}%</strong></span>
                              <span className="text-zinc-400">→</span>
                              <span className="font-data text-emerald-700">actual <strong>{r.actual ?? '–'}%</strong></span>
                              <span className={`font-data font-bold ml-2 ${Math.abs(r.error || 0) < 2 ? 'text-emerald-700' : Math.abs(r.error || 0) < 5 ? 'text-amber-700' : 'text-rose-700'}`}>
                                {r.error > 0 ? '+' : ''}{r.error}%
                              </span>
                              {!r.predictor_picked_adopted && (
                                <span className="text-[9px] px-1 py-0.5 rounded bg-zinc-200 text-zinc-600" title="Gebruiker koos een andere variant dan de predictor-winnaar">manual override</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-zinc-500">Bron:</span>
              <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden" data-testid="insights-mode-toggle">
                <button
                  onClick={() => setInsightsMode('coach')}
                  className={`px-3 py-1 text-[11px] font-medium transition-colors ${insightsMode === 'coach' ? 'bg-indigo-600 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
                  data-testid="insights-mode-coach"
                >
                  AI Coach scores
                </button>
                <button
                  onClick={() => setInsightsMode('outcome')}
                  className={`px-3 py-1 text-[11px] font-medium border-l border-zinc-300 transition-colors ${insightsMode === 'outcome' ? 'bg-emerald-600 text-white' : 'bg-white text-zinc-700 hover:bg-zinc-50'}`}
                  data-testid="insights-mode-outcome"
                >
                  📊 Echte MS Graph data
                </button>
              </div>
            </div>
            {insightsLoading && <p className="text-xs text-zinc-500">Patronen analyseren...</p>}
            {!insightsLoading && insights && insightsMode === 'outcome' && (
              <>
                {insights.note && (
                  <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-xs text-amber-800" data-testid="insights-outcome-note">
                    {insights.note}
                  </div>
                )}
                {(!insights.note && insights.entries > 0) && (
                  <>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-base">📊</span>
                        <h3 className="text-sm font-semibold text-emerald-900">Echte MS Graph engagement-metrics</h3>
                      </div>
                      <p className="text-xs text-zinc-700 mb-3">Gebaseerd op {insights.entries} sequences met daadwerkelijk verzonden e-mails ({insights.steps_analyzed} steps geanalyseerd).</p>
                      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-emerald-200 text-center">
                        <div>
                          <div className="font-data font-bold text-blue-700 text-lg">{insights.overall_avg?.open_rate ?? '–'}%</div>
                          <div className="text-[10px] text-zinc-500 uppercase">📬 open-rate</div>
                        </div>
                        <div>
                          <div className="font-data font-bold text-violet-700 text-lg">{insights.overall_avg?.click_rate ?? '–'}%</div>
                          <div className="text-[10px] text-zinc-500 uppercase">🖱 click-rate</div>
                        </div>
                        <div>
                          <div className="font-data font-bold text-emerald-700 text-lg">{insights.overall_avg?.reply_rate ?? '–'}%</div>
                          <div className="text-[10px] text-zinc-500 uppercase">💬 reply-rate</div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
                        <ChartLineUp size={14} className="text-emerald-600" />
                        Top outcome-patronen ({insights.rules?.length || 0})
                      </h3>
                      {(insights.rules || []).length === 0 && (
                        <p className="text-xs text-zinc-500 italic">Te weinig variatie of te weinig data. Verstuur meer campagnes en herlaad.</p>
                      )}
                      {(insights.rules || []).map((r, i) => {
                        const positive = r.lift_pct > 0;
                        const accent = positive ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300';
                        const txt = positive ? 'text-emerald-700' : 'text-rose-700';
                        const metricLabels = { open_rate: 'open-rate', click_rate: 'click-rate', reply_rate: 'reply-rate' };
                        return (
                          <div key={i} className={`rounded-md border-2 ${accent} p-3 flex items-start gap-3`} data-testid={`outcome-rule-${i}`}>
                            <div className={`flex-shrink-0 ${txt} font-data font-bold text-lg w-16 text-right`}>
                              {positive ? '+' : ''}{r.lift_pct}%
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-zinc-900">
                                {r.feature_label}: <span className="font-normal">{r.bucket}</span>
                              </div>
                              <div className="text-[11px] text-zinc-600 mt-0.5">
                                → {positive ? 'verhoogt' : 'verlaagt'} <strong>{metricLabels[r.metric] || r.metric}</strong>{' '}
                                ({r.in_avg}% vs {r.out_avg}% bij {r.step_count} steps)
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
            {!insightsLoading && insights && insightsMode === 'coach' && insights.entries === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <Lightbulb size={48} className="mx-auto mb-3 text-zinc-300" weight="duotone" />
                <p className="text-sm">Nog geen library data om te analyseren.</p>
                <p className="text-xs mt-1">Laat de Coach eerst minstens 1 sequence scoren ≥80.</p>
              </div>
            )}
            {!insightsLoading && insights && insightsMode === 'coach' && insights.entries > 0 && (
              <>
                <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb size={14} weight="fill" className="text-indigo-600" />
                    <h3 className="text-sm font-semibold text-indigo-900">Executive summary</h3>
                  </div>
                  <p className="text-xs text-zinc-700 leading-relaxed" data-testid="insights-exec-summary">
                    {insights.exec_summary || 'Geen patronen sterk genoeg om te rapporteren.'}
                  </p>
                  <div className="grid grid-cols-5 gap-2 mt-3 pt-3 border-t border-indigo-200 text-[10px]">
                    {[
                      { k: 'entries', l: 'sequences', v: insights.entries },
                      { k: 'steps_analyzed', l: 'steps', v: insights.steps_analyzed },
                      { k: 'uniqueness', l: 'uniqueness', v: insights.overall_avg?.uniqueness ?? '–' },
                      { k: 'cta_clarity', l: 'CTA-helderheid', v: insights.overall_avg?.cta_clarity ?? '–' },
                      { k: 'jargon', l: 'jargon', v: insights.overall_avg?.jargon ?? '–' },
                    ].map((s) => (
                      <div key={s.k} className="text-center" data-testid={`insights-stat-${s.k}`}>
                        <div className="font-data font-bold text-indigo-700 text-base">{s.v}</div>
                        <div className="text-zinc-500 uppercase tracking-wide">{s.l}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
                    <ChartLineUp size={14} className="text-indigo-600" />
                    Top patronen ({insights.rules?.length || 0})
                  </h3>
                  {(insights.rules || []).length === 0 && (
                    <p className="text-xs text-zinc-500 italic">Te weinig variatie in de data voor betrouwbare patronen. Voeg meer ≥80-sequences toe.</p>
                  )}
                  {(insights.rules || []).map((r, i) => {
                    const positive = r.lift_pct > 0;
                    const accent = positive ? 'bg-emerald-50 border-emerald-300' : 'bg-rose-50 border-rose-300';
                    const txt = positive ? 'text-emerald-700' : 'text-rose-700';
                    const dimLabels = { uniqueness: 'uniciteit', length: 'lengte', cta_clarity: 'CTA-helderheid', jargon: 'vakjargon' };
                    return (
                      <div key={i} className={`rounded-md border-2 ${accent} p-3 flex items-start gap-3`} data-testid={`insight-rule-${i}`}>
                        <div className={`flex-shrink-0 ${txt} font-data font-bold text-lg w-16 text-right`}>
                          {positive ? '+' : ''}{r.lift_pct}%
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-zinc-900">
                            {r.feature_label}: <span className="font-normal">{r.bucket}</span>
                          </div>
                          <div className="text-[11px] text-zinc-600 mt-0.5">
                            → {positive ? 'verhoogt' : 'verlaagt'} score op <strong>{dimLabels[r.dimension] || r.dimension}</strong>{' '}
                            ({r.score_in_bucket} vs {r.score_out_bucket} bij {r.step_count} steps)
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
